import { DRILL_STATUSES, type DrillPlanFilter } from '@felix/domain';
import { MultiSelect, Select } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import { usePmsQuery } from '../pms/PmsWorkParts.js';
import classes from './DrillPlanner.module.css';
import type { DrillPlanSource } from './plan-source.js';
import type { DrillPlan, DrillPlanner, DrillStatus } from './plan-types.js';
import type { DrillMark } from './plan-view.js';

/** Ekranın veri durumu. */
export type PlannerState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: DrillPlanner };

/** Planner ve panonun ortak verisi: tek uç (`…/drills/planner`), seçili gemiyle yeniden okunur. */
export function usePlannerData(source: DrillPlanSource, vesselId: string | null) {
  const [state, setState] = useState<PlannerState>({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', data: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);
  return { state, reload: load };
}

/** Sunucu hata kodunun metni: önce tatbikat planı, sonra kütüphane, yoksa genel. */
export function useDrillErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    return t(`drillPlan.errors.${code}`, {
      defaultValue: t(`drills.errors.${code}`, { defaultValue: t('drillPlan.errors.GENERIC') }),
    });
  };
}

const STATUS_TONE: Record<DrillStatus, StatusTone> = {
  draft: 'neutral',
  planned: 'info',
  due: 'warning',
  in_progress: 'info',
  pending_review: 'warning',
  completed: 'success',
  overdue: 'danger',
  postponed: 'warning',
  cancelled: 'neutral',
};

export function DrillStatusBadge({ status }: { status: DrillStatus }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={STATUS_TONE[status]} withIcon={status === 'overdue'}>
      {t(`drillPlan.status.${status}`)}
    </StatusBadge>
  );
}

const REVIEW_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  approved: 'success',
  returned: 'danger',
  in_effect: 'success',
  ended: 'neutral',
  replaced: 'neutral',
  draft: 'neutral',
};

/** Erteleme etkisi, uygulama ve varyant kararı rozeti (bekliyor / onaylı / iade / sona erdi). */
export function ReviewBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={REVIEW_TONE[value] ?? 'neutral'}>
      {t(`drillPlan.review.${value}`)}
    </StatusBadge>
  );
}

export function MarkDot({ mark }: { mark: DrillMark }) {
  const { t } = useTranslation();
  return (
    <span
      className={classes.mark}
      data-mark={mark}
      role="img"
      aria-label={t(`drillPlan.marks.${mark}`)}
      title={t(`drillPlan.marks.${mark}`)}
    />
  );
}

export function MarkLegend() {
  const { t } = useTranslation();
  return (
    <div className={classes.legend}>
      {(['done', 'due', 'postponed', 'overdue'] as const).map((mark) => (
        <span key={mark}>
          <span className={classes.mark} data-mark={mark} aria-hidden />
          {t(`drillPlan.marks.${mark}`)}
        </span>
      ))}
    </div>
  );
}

/** Tatbikat hücresi: kod ve ad; ad ayrıntıyı açar. */
export function DrillCell({ plan, onOpen }: { plan: DrillPlan; onOpen?: () => void }) {
  return (
    <span className={classes.drill}>
      <span className={classes.drillCode}>{plan.templateCode}</span>
      {onOpen ? (
        <button type="button" className={classes.linkButton} onClick={onOpen}>
          {plan.templateName}
        </button>
      ) : (
        <span className={classes.drillName}>{plan.templateName}</span>
      )}
    </span>
  );
}

const ALL = 'all';

/** Süzgecin adres sorgusu: `vessel`, `status` (virgüllü), `category`, `responsible`, `from`, `to`. */
export function useDrillFilter() {
  const [query, setQuery] = usePmsQuery();
  const statuses = (query.get('status') ?? '')
    .split(',')
    .filter((s): s is DrillStatus => (DRILL_STATUSES as readonly string[]).includes(s));
  const vesselId = query.get('vessel');
  const category = query.get('category');
  const responsible = query.get('responsible');
  const from = query.get('from');
  const to = query.get('to');
  const filter: DrillPlanFilter = {
    statuses,
    ...(category ? { categoryIds: [category] } : {}),
    ...(responsible ? { responsibleUserIds: [responsible] } : {}),
    from,
    to,
  };
  return { query, setQuery, vesselId, filter, statuses, category, responsible, from, to };
}

/**
 * Süzgeç çubuğu (ADR 0108 Ek kalıbı): gemi (ofiste), durumlar ("ya da"), tür, sorumlu, tarih
 * aralığı. Gecikmiş ve ertelenmiş tatbikat tarih aralığına bakmaz.
 */
export function DrillFilters({
  data,
  state,
  dates = true,
}: {
  data: DrillPlanner;
  state: ReturnType<typeof useDrillFilter>;
  dates?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`drillPlan.filters.${key}`);
  const categories = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of data.plans) map.set(p.categoryId, p.categoryName ?? p.categoryId);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [data.plans]);
  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of data.plans) {
      if (p.responsibleUserId) map.set(p.responsibleUserId, p.responsibleName ?? '');
    }
    return [...map].map(([value, label]) => ({ value, label }));
  }, [data.plans]);
  const set = state.setQuery;
  return (
    <div className={classes.filters}>
      {data.side === 'office' ? (
        <Select
          label={k('vessel')}
          data={[
            { value: ALL, label: k('allVessels') },
            ...data.vessels.map((v) => ({ value: v.id, label: v.name })),
          ]}
          value={state.vesselId ?? ALL}
          allowDeselect={false}
          onChange={(value) => set({ vessel: value === ALL ? null : value, plan: null })}
        />
      ) : null}
      <MultiSelect
        label={k('status')}
        placeholder={state.statuses.length === 0 ? k('allStatuses') : undefined}
        data={DRILL_STATUSES.filter((s) => s !== 'in_progress' && s !== 'completed').map((s) => ({
          value: s,
          label: t(`drillPlan.status.${s}`),
        }))}
        value={state.statuses}
        clearable
        clearButtonProps={{ 'aria-label': k('clear') }}
        onChange={(values) => set({ status: values.length === 0 ? null : values.join(',') })}
      />
      <Select
        label={k('type')}
        placeholder={k('allTypes')}
        data={categories}
        value={state.category}
        clearable
        clearButtonProps={{ 'aria-label': k('clear') }}
        onChange={(value) => set({ category: value })}
      />
      <Select
        label={k('responsible')}
        placeholder={k('anyone')}
        data={people}
        value={state.responsible}
        clearable
        clearButtonProps={{ 'aria-label': k('clear') }}
        onChange={(value) => set({ responsible: value })}
      />
      {dates ? (
        <>
          <DayInput
            label={k('from')}
            value={state.from}
            clearable
            onChange={(value) => set({ from: value })}
          />
          <DayInput
            label={k('to')}
            value={state.to}
            clearable
            onChange={(value) => set({ to: value })}
          />
        </>
      ) : null}
    </div>
  );
}

/** Ay adı (`YYYY-MM`), dile göre. */
export function useMonthName() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-GB';
  return (month: string, style: 'long' | 'short' = 'long') => {
    const [y, m] = month.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, {
      month: style,
      ...(style === 'long' ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, 1)));
  };
}

export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = (y ?? 2000) * 12 + (m ?? 1) - 1 + by;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}
